import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Search, Check } from 'lucide-react';
import type { Product } from '../../types/@types/product';
import type { Category } from '../../types/@types/category';
import { safeArray } from '../../utils/safe';
import CategoryBar from '../CategoryBar';
import { formatCurrency } from '../../utils/format-currency';
import { Button } from '../ui';

interface ProductPickerProps {
  products: Product[];
  categories: Category[];
  activeCategory: string | null;
  onCategorySelect: (categoryId: string | null) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onProductClick: (product: Product) => void;
  pendingProductId?: string | null;
  isAddingProduct?: boolean;
  onGoToOrder?: () => void;
  onScrollPast?: (isPast: boolean) => void;
  loading?: boolean;
}

const DENSE_BREAKPOINT = 360;

export function ProductPicker({
  products,
  categories,
  activeCategory,
  onCategorySelect,
  searchTerm,
  onSearchTermChange,
  onProductClick,
  pendingProductId,
  isAddingProduct,
  onGoToOrder,
  onScrollPast,
  loading,
}: ProductPickerProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [panelWidth, setPanelWidth] = useState(0);
  const [denseOverride, setDenseOverride] = useState<boolean | null>(null);
  // La búsqueda arranca colapsada: el teclado solo aparece si el mesero la abre
  const [showSearch, setShowSearch] = useState(false);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || (e.target as HTMLElement)?.isContentEditable;

      if (e.key === '/' && !isEditable) {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        }, 50);
      }

      if (e.key === 'Escape' && isEditable && e.target === searchInputRef.current) {
        onSearchTermChange('');
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onSearchTermChange]);

  const filteredProducts = useMemo(() => safeArray(products), [products]);
  // Por defecto mostramos GRID (más rápido para tocar), no lista densa.
  const denseList = denseOverride ?? false;
  const effectiveColumns = denseList
    ? 1
    // En móvil (<480px) mínimo 2 columnas; en pantallas anchas, según el espacio
    : Math.max(panelWidth > 0 && panelWidth < 480 ? 2 : 1, Math.floor(panelWidth / 240) || 1);
  const totalRows = filteredProducts.length > 0 ? Math.ceil(filteredProducts.length / effectiveColumns) : 0;
  const shouldVirtualize = filteredProducts.length > 12;

  useEffect(() => {
    if (!onScrollPast && !onGoToOrder) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let animationFrame: number | null = null;
    const handleScroll = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        const threshold = scroller.clientHeight;
        const next = scroller.scrollTop > threshold;
        setScrolledPast(next);
        onScrollPast?.(next);
        animationFrame = null;
      });
    };

    handleScroll();
    scroller.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      scroller.removeEventListener('scroll', handleScroll);
    };
  }, [onScrollPast, onGoToOrder]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setPanelWidth(entry.contentRect.width);
    });

    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (panelWidth >= DENSE_BREAKPOINT && denseOverride !== null) {
      setDenseOverride(null);
    }
  }, [panelWidth, denseOverride]);

  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? totalRows : 0,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => (denseList ? 156 : 220),
    overscan: 8,
  });

  const allowDenseToggle = panelWidth > 0 && panelWidth < 420;

  const renderProductCard = (product: Product) => {
    const isPending = isAddingProduct && String(pendingProductId) === String(product.id);
    return (
      <article
        key={product.id}
        role="listitem"
        className="flex h-full flex-col justify-between gap-3 rounded-2xl border border-divider bg-surface p-4 shadow-sm shadow-slate-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-slate-300 dark:hover:border-neutral-700 focus-within:ring-2 focus-within:ring-[color:var(--primary)]/70 active:scale-[0.99]"
        style={{ borderRadius: 'calc(var(--radius) * 0.9)' }}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 className="truncate text-base font-bold text-[color:var(--text)] tracking-tight">
            {product.name}
          </h3>
          <p className="text-xs text-[color:var(--text-muted)]">
            {typeof product.category === 'string'
              ? product.category
              : product.category?.name}
          </p>
          <div className="mt-auto pt-2 flex items-center justify-between">
            <span className="inline-flex items-center rounded-lg bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-1 text-sm font-bold text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30">
              {formatCurrency(product.price)}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onProductClick(product)}
            disabled={isPending}
            size="lg"
            className={`w-full border-emerald-600/30 text-emerald-750 hover:bg-emerald-700 hover:border-emerald-700 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10 hover:text-white transition-all duration-300 active:scale-[0.97] hover:shadow-sm ${
              isPending
                ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-500 scale-95'
                : ''
            }`}
            aria-label={`Agregar ${product.name}`}
          >
            {isPending
              ? <><Check size={16} className="mr-1 animate-bounce" /> ¡Agregado!</>
              : 'Agregar'}
          </Button>
        </div>
      </article>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between px-4 pb-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            Productos
          </p>
          <h2 className="text-[clamp(20px,2.6vw,24px)] font-semibold text-[color:var(--surface-contrast)]">
            Selecciona y agrega al pedido
          </h2>
        </div>
        {allowDenseToggle ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={denseList}
            onClick={() => setDenseOverride((prev) => (prev === null ? !denseList : !prev))}
            className="ml-3 min-h-[40px] px-4 text-xs font-semibold"
          >
            {denseList ? 'Ver como grid' : 'Ver lista densa'}
          </Button>
        ) : null}
      </div>
      <CategoryBar
        categories={categories}
        activeId={activeCategory}
        onSelect={onCategorySelect}
      />
      <div className="px-4 pb-2 pt-4">
        <label htmlFor="product-search" className="sr-only">
          Buscar producto por nombre
        </label>
        {showSearch || searchTerm ? (
          <div className="relative">
            <input
              ref={searchInputRef}
              id="product-search"
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              onFocus={(e) => {
                const el = e.currentTarget;
                setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
              }}
              placeholder="Buscar producto"
              className="peer w-full rounded-full border border-divider bg-surface pl-9 pr-16 py-3 text-sm text-[color:var(--text)] placeholder:text-slate-500 shadow-sm focus:outline-none focus:ring-4 focus:ring-[color:var(--primary)]/10 focus:border-[color:var(--primary)] transition-all duration-300"
            />
            <Search size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 peer-focus:text-[color:var(--primary)] peer-focus:scale-110 transition-all duration-300" />
            <button
              type="button"
              onClick={() => { onSearchTermChange(''); setShowSearch(false); searchInputRef.current?.blur(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300 text-sm font-bold"
              aria-label="Cerrar búsqueda"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setShowSearch(true);
              setTimeout(() => searchInputRef.current?.focus(), 50);
            }}
            className="flex w-full items-center gap-2 rounded-full border border-divider bg-surface px-4 py-3 text-sm text-slate-400 shadow-sm transition hover:border-[color:var(--primary)]/50 focus:outline-none focus:ring-4 focus:ring-[color:var(--primary)]/10"
          >
            <Search size={16} />
            Buscar producto…
          </button>
        )}
      </div>
      <div className="relative flex-1">
        <div
          ref={scrollerRef}
          className="ProductsPanel relative flex-1 overflow-y-auto px-4"
          role="list"
          aria-label="Listado de productos"
          style={{
            maxHeight: 'calc(100dvh - var(--pos-products-offset, 18rem))',
            paddingBottom: 'calc(var(--pos-bottom-nav-height, 4.5rem) + var(--safe-area-bottom))',
          }}
        >
          {loading ? (
            <div
              className="grid gap-3 sm:gap-4"
              style={{
                gridTemplateColumns: `repeat(${denseList ? 1 : effectiveColumns}, minmax(0, 1fr))`,
              }}
            >
              {[...Array(8)].map((_, idx) => (
                <div
                  key={idx}
                  className="animate-pulse flex flex-col justify-between h-[180px] rounded-2xl border border-divider/60 bg-surface p-4 space-y-4"
                  style={{ borderRadius: 'calc(var(--radius) * 0.9)' }}
                >
                  <div className="space-y-2">
                    <div className="h-4 w-3/4 rounded bg-slate-200 dark:bg-neutral-800" />
                    <div className="h-3 w-1/2 rounded bg-slate-200 dark:bg-neutral-800" />
                  </div>
                  <div className="h-6 w-1/3 rounded bg-slate-200 dark:bg-neutral-800 mt-auto" />
                  <div className="h-10 w-full rounded-full bg-slate-200 dark:bg-neutral-800" />
                </div>
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            shouldVirtualize ? (
              <div
                style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
                className="relative"
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const startIndex = virtualRow.index * effectiveColumns;
                  const rowItems = filteredProducts.slice(
                      startIndex,
                      startIndex + effectiveColumns,
                  );

                  if (rowItems.length === 0) {
                    return null;
                  }

                  return (
                    <div
                      key={virtualRow.key}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      className="absolute inset-x-0"
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      <div
                        className="grid gap-3 sm:gap-4"
                        style={{
                          gridTemplateColumns: `repeat(${denseList ? 1 : effectiveColumns}, minmax(0, 1fr))`,
                        }}
                      >
                        {rowItems.map(renderProductCard)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className="grid gap-3 sm:gap-4"
                style={{
                  gridTemplateColumns: `repeat(${denseList ? 1 : effectiveColumns}, minmax(0, 1fr))`,
                }}
              >
                {filteredProducts.map(renderProductCard)}
              </div>
            )
          ) : (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center text-[color:var(--text-muted)]">
              <p className="text-sm font-medium">No encontramos productos.</p>
              <button
                type="button"
                onClick={() => onSearchTermChange('')}
                className="rounded-full border border-divider px-4 py-2 text-sm font-semibold text-[color:var(--text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)]/70"
              >
                Ver todos
              </button>
            </div>
          )}
        </div>
        {scrolledPast && onGoToOrder ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center">
            <button
              type="button"
              onClick={onGoToOrder}
              className="pointer-events-auto inline-flex min-h-[44px] items-center gap-2 rounded-full border border-divider bg-surface px-5 text-sm font-semibold text-[color:var(--text)] shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)]/70"
            >
              Ir al pedido
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ProductPicker;
